import React, { useState, useEffect } from 'react';
import { Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Box, Grid, TextField, Modal, FormControl, InputLabel, Select, MenuItem, FormHelperText, TableSortLabel, IconButton, Pagination, Stack, Checkbox, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { it } from 'date-fns/locale';
import { useForm, Controller } from 'react-hook-form';
import { Edit as EditIcon, Delete as DeleteIcon, FilterList, Clear, RestartAlt as RestartAltIcon, ChevronLeft, ChevronRight, FileDownload, Description } from '@mui/icons-material';
import api from '../utils/api';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import jsPDF from 'jspdf';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { canViewAccountingMovements } from '../utils/roles';

const AccountingJournalPage = () => {
  const { user } = useAuth();
  const canViewMovements = canViewAccountingMovements(user);

  // Stati per i dati e i filtri
  const [movements, setMovements] = useState([]);
  const [filteredMovements, setFilteredMovements] = useState([]);
  const [sortedMovements, setSortedMovements] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date())
  });
  // Aggiungo uno stato per il filtro conto
  const [selectedAccount, setSelectedAccount] = useState('all');
  
  // Aggiungo stato per la ricerca nella descrizione
  const [descriptionSearch, setDescriptionSearch] = useState('');
  
  // Stato per l'ordinamento
  const [order, setOrder] = useState({
    field: 'date',
    direction: 'desc'
  });

  // Stati per i totali
  const [totals, setTotals] = useState({
    income: 0,
    expense: 0,
    balance: 0,
    previousBalance: 0
  });

  // React Hook Form per la validazione
  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      description: '',
      amount: '',
      date: new Date(),
      type: 'IN',
      paymentMethodId: '',
      accountId: '',
      commissioni: ''
    }
  });

  const [editMovement, setEditMovement] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState(null);
  const [openExportDialog, setOpenExportDialog] = useState(false);

  // Filtri colonne
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    date: '',
    type: '',
    description: '',
    paymentMethod: '',
    account: '',
    amount: '',
    source: '',
    flag: 'all' // 'all', 'selected', 'notSelected'
  });

  // Larghezze colonne
  const getDefaultColumnWidths = () => ({
    date: 120,
    type: 100,
    description: 250,
    paymentMethod: 150,
    account: 150,
    amount: 120,
    source: 120,
    flag: 80,
    actions: 120
  });

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('accountingJournal_columnWidths');
    return saved ? JSON.parse(saved) : getDefaultColumnWidths();
  });

  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  // Paginazione
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Flag selezionati
  const [selectedFlags, setSelectedFlags] = useState(new Set());

  // Caricamento iniziale dei dati
  useEffect(() => {
    fetchMovements();
    fetchPaymentMethods();
    fetchAccounts();
  }, []);

  // Applicazione filtri quando cambiano i movimenti o il range di date
  useEffect(() => {
    filterMovements();
  }, [movements, dateRange, selectedAccount, descriptionSearch, columnFilters]);

  // Calcolo totali quando cambiano i movimenti filtrati
  useEffect(() => {
    calculateTotals();
  }, [filteredMovements]);

  // Ordinamento dei dati quando cambiano i movimenti filtrati o l'ordinamento
  useEffect(() => {
    const sorted = sortData();
    if (sorted) {
      setSortedMovements(sorted);
      // Reset alla prima pagina quando cambiano i dati
      setCurrentPage(1);
    }
  }, [filteredMovements, order]);

  // Calcola i dati paginati
  const totalPages = Math.ceil(sortedMovements.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMovements = sortedMovements.slice(startIndex, endIndex);

  // Gestione cambio pagina
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  // Gestione flag selezionati
  const handleToggleFlag = (movementId) => {
    setSelectedFlags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(movementId)) {
        newSet.delete(movementId);
      } else {
        newSet.add(movementId);
      }
      return newSet;
    });
  };

  const fetchMovements = async () => {
    setLoading(true);
    try {
      const response = await api.get('/accounting-movements');
      logger.debug('📊 Movimenti contabili caricati:', response.data.length);
      setMovements(response.data);
    } catch (error) {
      logger.error('Errore nel caricamento dei movimenti:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get('/payment-methods');
      setPaymentMethods(response.data);
    } catch (error) {
      logger.error('Errore nel caricamento dei metodi di pagamento:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/accounts');
      setAccounts(response.data);
    } catch (error) {
      logger.error('Errore nel caricamento dei conti:', error);
    }
  };

  const filterMovements = () => {
    if (!movements.length) return;
    
    const filtered = movements.filter(movement => {
      const movementDate = parseISO(movement.date);
      const dateInRange = movementDate >= dateRange.startDate && 
             movementDate <= dateRange.endDate;
      
      // Filtro per conto
      const accountMatches = selectedAccount === 'all' || 
                            movement.accountId === parseInt(selectedAccount);
      
      // Filtro per descrizione
      const descriptionMatches = descriptionSearch === '' || 
                                movement.description?.toLowerCase().includes(descriptionSearch.toLowerCase());
      
      // Filtri colonne
      const dateMatches = !columnFilters.date || 
                         format(parseISO(movement.date), 'dd/MM/yyyy').includes(columnFilters.date);
      
      const typeMatches = !columnFilters.type || 
                         getMovementTypeText(movement.type).toLowerCase().includes(columnFilters.type.toLowerCase());
      
      const descriptionColumnMatches = !columnFilters.description || 
                                      movement.description?.toLowerCase().includes(columnFilters.description.toLowerCase());
      
      const paymentMethodMatches = !columnFilters.paymentMethod || 
                                  getPaymentMethodName(movement.paymentMethodId).toLowerCase().includes(columnFilters.paymentMethod.toLowerCase());
      
      const accountColumnMatches = !columnFilters.account || 
                                  getAccountName(movement.accountId).toLowerCase().includes(columnFilters.account.toLowerCase());
      
      const amountMatches = !columnFilters.amount || 
                           formatCurrency(movement.amount).includes(columnFilters.amount);
      
      const sourceMatches = !columnFilters.source || 
                           getSourceText(movement.source).toLowerCase().includes(columnFilters.source.toLowerCase());
      
      // Filtro flag
      let flagMatches = true;
      if (columnFilters.flag === 'selected') {
        flagMatches = selectedFlags.has(movement.id);
      } else if (columnFilters.flag === 'notSelected') {
        flagMatches = !selectedFlags.has(movement.id);
      }
      // Se è 'all', flagMatches rimane true
      
      return dateInRange && accountMatches && descriptionMatches && 
             dateMatches && typeMatches && descriptionColumnMatches && 
             paymentMethodMatches && accountColumnMatches && amountMatches && sourceMatches && flagMatches;
    });
    
    setFilteredMovements(filtered);
  };

  // Funzione per gestire l'ordinamento
  const sortData = () => {
    if (!filteredMovements.length) return;
    
    const { field, direction } = order;
    
    const sortedData = [...filteredMovements].sort((a, b) => {
      let valueA, valueB;
      
      // Determina i valori da confrontare in base al campo
      switch (field) {
        case 'date':
          valueA = new Date(a.date);
          valueB = new Date(b.date);
          break;
        case 'type':
          valueA = a.type;
          valueB = b.type;
          break;
        case 'description':
          valueA = a.description?.toLowerCase() || '';
          valueB = b.description?.toLowerCase() || '';
          break;
        case 'paymentMethodId':
          valueA = getPaymentMethodName(a.paymentMethodId).toLowerCase();
          valueB = getPaymentMethodName(b.paymentMethodId).toLowerCase();
          break;
        case 'accountId':
          valueA = getAccountName(a.accountId).toLowerCase();
          valueB = getAccountName(b.accountId).toLowerCase();
          break;
        case 'amount':
          valueA = parseFloat(a.amount);
          valueB = parseFloat(b.amount);
          break;
        case 'source':
          valueA = a.source?.toLowerCase() || '';
          valueB = b.source?.toLowerCase() || '';
          break;
        default:
          valueA = a[field];
          valueB = b[field];
      }
      
      // Confronta i valori
      if (valueA < valueB) {
        return direction === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return sortedData;
  };

  // Funzione per gestire il click sull'intestazione della colonna
  const handleRequestSort = (field) => {
    const isAsc = order.field === field && order.direction === 'asc';
    setOrder({
      field,
      direction: isAsc ? 'desc' : 'asc'
    });
  };

  const calculateTotals = () => {
    // Solo i movimenti del conto selezionato (stessa logica di filterMovements)
    const byAccount = (m) => selectedAccount === 'all' || m.accountId === parseInt(selectedAccount);
    const movementsForAccount = movements.filter(byAccount);

    if (!filteredMovements.length) {
      // Anche se non ci sono movimenti nel periodo, il saldo precedente va comunque calcolato (per il conto selezionato)
      const prev = movementsForAccount.reduce((acc, m) => {
        const movementDate = parseISO(m.date);
        if (movementDate < dateRange.startDate) {
          const amount = parseFloat(m.amount) || 0;
          if (m.type === 'IN') acc += amount;
          if (m.type === 'OUT') acc -= amount;
        }
        return acc;
      }, 0);

      setTotals({ income: 0, expense: 0, balance: 0, previousBalance: prev });
      return;
    }

    const income = filteredMovements
      .filter(m => m.type === 'IN')
      .reduce((sum, m) => sum + parseFloat(m.amount), 0);
    
    const expense = filteredMovements
      .filter(m => m.type === 'OUT')
      .reduce((sum, m) => sum + parseFloat(m.amount), 0);

    // Saldo precedente: solo movimenti del conto selezionato, con data < inizio periodo
    const previousBalance = movementsForAccount.reduce((acc, m) => {
      const movementDate = parseISO(m.date);
      if (movementDate < dateRange.startDate) {
        const amount = parseFloat(m.amount) || 0;
        if (m.type === 'IN') acc += amount;
        if (m.type === 'OUT') acc -= amount;
      }
      return acc;
    }, 0);
    
    setTotals({
      income,
      expense,
      balance: income - expense,
      previousBalance
    });
  };

  const handleOpenModal = () => {
    setEditMovement(null);
    reset({
      description: '',
      amount: '',
      date: new Date(),
      type: 'IN',
      paymentMethodId: '',
      accountId: '',
      commissioni: ''
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditMovement(null);
  };

  const handleOpenEditModal = (movement) => {
    setEditMovement(movement);
    reset({
      description: movement.description,
      amount: movement.amount,
      date: parseISO(movement.date),
      type: movement.type,
      paymentMethodId: movement.paymentMethodId,
      accountId: movement.accountId,
      commissioni: ''
    });
    setOpenModal(true);
  };

  const handleOpenDeleteDialog = (movement) => {
    setMovementToDelete(movement);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setMovementToDelete(null);
  };

  const handleDeleteMovement = async () => {
    if (!movementToDelete) return;
    try {
      await api.delete(`/accounting-movements/${movementToDelete.id}`);
      fetchMovements();
    } catch (err) {
      console.error('Errore durante l\'eliminazione:', err);
    }
    handleCloseDeleteDialog();
  };

  const onSubmit = async (data) => {
    try {
      let importo = data.amount;
      if (typeof importo === 'string') {
        importo = importo.replace(',', '.');
      }
      const formattedData = {
        ...data,
        date: format(data.date, 'yyyy-MM-dd'),
        amount: parseFloat(importo),
        origin: 'MANUAL'
      };
      if (editMovement) {
        if (data.commissioni !== '' && data.commissioni != null) {
          const comm = typeof data.commissioni === 'string' ? parseFloat(data.commissioni.replace(',', '.')) : parseFloat(data.commissioni);
          if (!Number.isNaN(comm) && comm > 0) {
            formattedData.commissioni = comm;
          }
        }
        await api.put(`/accounting-movements/${editMovement.id}`, formattedData);
      } else {
      await api.post('/accounting-movements', formattedData);
      }
      handleCloseModal();
      setEditMovement(null);
      fetchMovements();
    } catch (error) {
      console.error('Errore durante il salvataggio del movimento:', error);
      if (error.response) {
        console.error('Dettagli errore:', error.response.data);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  const getMovementTypeText = (type) => {
    return type === 'IN' ? 'Entrata' : 'Uscita';
  };

  const getSourceText = (source) => {
    switch (source) {
      case 'sale': return 'Vendita';
      case 'maintenance': return 'Manutenzione';
      case 'manual': return 'Manuale';
      default: return source;
    }
  };

  const getPaymentMethodName = (id) => {
    const method = paymentMethods.find(m => m.id === id);
    return method ? method.name : 'N/A';
  };

  const getAccountName = (id) => {
    const account = accounts.find(a => a.id === id);
    return account ? account.name : 'N/A';
  };

  const handleDateRangeChange = (field, date) => {
    setDateRange(prev => ({
      ...prev,
      [field]: date
    }));
  };

  // Gestione filtri colonne
  const handleFilterChange = (filterName, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleClearFilters = () => {
    setColumnFilters({
      date: '',
      type: '',
      description: '',
      paymentMethod: '',
      account: '',
      amount: '',
      source: '',
      flag: 'all'
    });
  };

  // Gestione resize colonne
  const handleResetColumnWidths = () => {
    const defaults = getDefaultColumnWidths();
    setColumnWidths(defaults);
    localStorage.setItem('accountingJournal_columnWidths', JSON.stringify(defaults));
  };

  const handleResizeStart = (columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnKey);
    setResizeStartX(e.clientX);
    setResizeStartWidth(columnWidths[columnKey]);
  };

  // Handle column resize
  useEffect(() => {
    const handleResize = (e) => {
      if (resizingColumn) {
        const diff = e.clientX - resizeStartX;
        const newWidth = Math.max(50, resizeStartWidth + diff); // Minimum width 50px
        setColumnWidths(prev => ({
          ...prev,
          [resizingColumn]: newWidth
        }));
      }
    };

    const handleResizeEnd = () => {
      if (resizingColumn) {
        setColumnWidths(prev => {
          const updated = { ...prev };
          localStorage.setItem('accountingJournal_columnWidths', JSON.stringify(updated));
          return updated;
        });
        setResizingColumn(null);
      }
    };

    if (resizingColumn) {
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  const handleExportOrPrint = () => {
    setOpenExportDialog(true);
  };

  const handleExportToExcel = () => {
    try {
      // Prepara i dati per l'esportazione
      let exportData = sortedMovements.map(movement => ({
        'Data': format(parseISO(movement.date), 'dd/MM/yyyy'),
        'Tipo': getMovementTypeText(movement.type),
        'Descrizione': movement.description || '',
        'Metodo di Pagamento': getPaymentMethodName(movement.paymentMethodId),
        'Conto': getAccountName(movement.accountId),
        'Importo': formatCurrency(movement.amount),
        'Origine': getSourceText(movement.source)
      }));

      // Aggiungi una riga di totali se necessario
      if (totals.income > 0 || totals.expense > 0) {
        const totalRow = {
          'Data': '',
          'Tipo': '',
          'Descrizione': '',
          'Metodo di Pagamento': '',
          'Conto': 'Totale Entrate:',
          'Importo': formatCurrency(totals.income),
          'Origine': ''
        };
        const expenseRow = {
          'Data': '',
          'Tipo': '',
          'Descrizione': '',
          'Metodo di Pagamento': '',
          'Conto': 'Totale Uscite:',
          'Importo': formatCurrency(totals.expense),
          'Origine': ''
        };
        const balanceRow = {
          'Data': '',
          'Tipo': '',
          'Descrizione': '',
          'Metodo di Pagamento': '',
          'Conto': 'Saldo:',
          'Importo': formatCurrency(totals.balance),
          'Origine': ''
        };
        
        // Aggiungi i totali ai dati
        exportData.push(totalRow, expenseRow, balanceRow);
      }

      // Crea il worksheet con tutti i dati (inclusi i totali)
      const worksheet = window.XLSX.utils.json_to_sheet(exportData);
      
      // Imposta la larghezza delle colonne
      const columnWidths = [
        { wch: 12 }, // Data
        { wch: 10 }, // Tipo
        { wch: 40 }, // Descrizione
        { wch: 20 }, // Metodo di Pagamento
        { wch: 20 }, // Conto
        { wch: 15 }, // Importo
        { wch: 15 }  // Origine
      ];
      worksheet['!cols'] = columnWidths;

      // Crea il workbook
      const workbook = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimenti Contabili');

      // Genera il file Excel
      const excelBuffer = window.XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Crea un URL per il blob e avvia il download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nome file con data
      const fileName = `Registro_Movimenti_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      link.download = fileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Pulisci l'URL
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);

      setOpenExportDialog(false);
    } catch (error) {
      console.error('Errore nell\'esportazione Excel:', error);
      alert('Errore nell\'esportazione in Excel. Riprova.');
    }
  };

  const handlePrint = () => {
    setOpenExportDialog(false);
    try {
      // Crea il documento PDF
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const startY = 20;
      let currentY = startY;
      
      // Configurazione font
      doc.setFont('helvetica');
      
      // Titolo
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Registro Movimenti Contabili', pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;
      
      // Periodo se ci sono filtri data
      if (dateRange.startDate && dateRange.endDate) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const periodText = `Periodo: ${format(dateRange.startDate, 'dd/MM/yyyy')} - ${format(dateRange.endDate, 'dd/MM/yyyy')}`;
        doc.text(periodText, pageWidth / 2, currentY, { align: 'center' });
        currentY += 8;
      }
      
      // Configurazione colonne
      const colWidths = [
        (pageWidth - margin * 2) * 0.12, // Data
        (pageWidth - margin * 2) * 0.10, // Tipo
        (pageWidth - margin * 2) * 0.25, // Descrizione
        (pageWidth - margin * 2) * 0.15, // Metodo Pagamento
        (pageWidth - margin * 2) * 0.15, // Conto
        (pageWidth - margin * 2) * 0.13, // Importo
        (pageWidth - margin * 2) * 0.10  // Origine
      ];
      
      const headerHeight = 8;
      const rowHeight = 7;
      const maxRowsPerPage = Math.floor((pageHeight - currentY - 30) / rowHeight); // 30 per margini e totali
      
      // Funzione per disegnare una riga della tabella
      const drawRow = (y, data, isHeader = false) => {
        let x = margin;
        data.forEach((text, index) => {
          doc.setFontSize(isHeader ? 10 : 9);
          doc.setFont('helvetica', isHeader ? 'bold' : 'normal');
          
          // Disegna bordo
          doc.setDrawColor(0, 0, 0);
          doc.rect(x, y - rowHeight + 2, colWidths[index], rowHeight, 'S');
          
          // Testo
          const align = index === 5 ? 'right' : 'left'; // Importo allineato a destra
          const padding = 3;
          const textX = align === 'right' ? x + colWidths[index] - padding : x + padding;
          
          // Tronca il testo se troppo lungo
          const maxWidth = colWidths[index] - padding * 2;
          let displayText = text;
          if (!isHeader && doc.getTextWidth(text) > maxWidth) {
            displayText = doc.splitTextToSize(text, maxWidth)[0] + '...';
          }
          
          doc.text(displayText, textX, y, { align });
          x += colWidths[index];
        });
      };
      
      // Intestazione tabella
      const headerData = ['Data', 'Tipo', 'Descrizione', 'Metodo Pagamento', 'Conto', 'Importo', 'Origine'];
      drawRow(currentY, headerData, true);
      currentY += rowHeight;
      
      // Riga separatrice
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, currentY - 2, pageWidth - margin, currentY - 2);
      
      // Dividi i movimenti in pagine
      const totalPages = Math.ceil(sortedMovements.length / maxRowsPerPage) || 1;
      let movementIndex = 0;
      
      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        if (pageIndex > 0) {
          doc.addPage();
          currentY = startY;
          
          // Ripeti intestazione su ogni pagina
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text('Registro Movimenti Contabili', pageWidth / 2, currentY, { align: 'center' });
          currentY += 10;
          
          if (dateRange.startDate && dateRange.endDate) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const periodText = `Periodo: ${format(dateRange.startDate, 'dd/MM/yyyy')} - ${format(dateRange.endDate, 'dd/MM/yyyy')}`;
            doc.text(periodText, pageWidth / 2, currentY, { align: 'center' });
            currentY += 8;
          }
          
          // Intestazione tabella
          drawRow(currentY, headerData, true);
          currentY += rowHeight;
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, currentY - 2, pageWidth - margin, currentY - 2);
        }
        
        // Numero pagina
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(128, 128, 128);
        doc.text(`Pagina ${pageIndex + 1} di ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        doc.setTextColor(0, 0, 0);
        
        // Aggiungi righe per questa pagina
        const endIndex = Math.min(movementIndex + maxRowsPerPage, sortedMovements.length);
        for (let i = movementIndex; i < endIndex; i++) {
          const movement = sortedMovements[i];
          const rowData = [
            format(parseISO(movement.date), 'dd/MM/yyyy'),
            getMovementTypeText(movement.type),
            movement.description || '',
            getPaymentMethodName(movement.paymentMethodId),
            getAccountName(movement.accountId),
            formatCurrency(movement.amount),
            getSourceText(movement.source)
          ];
          
          drawRow(currentY, rowData, false);
          currentY += rowHeight;
          
          // Controlla se serve una nuova pagina
          if (currentY + rowHeight > pageHeight - 20 && i < sortedMovements.length - 1) {
            break;
          }
        }
        
        movementIndex = endIndex;
        
        // Se è l'ultima pagina, aggiungi i totali
        if (pageIndex === totalPages - 1) {
          currentY += 5;
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, currentY, pageWidth - margin, currentY);
          currentY += 5;
          
          // Totale Entrate
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          const totalIncomeX = margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4];
          doc.text('Totale Entrate:', totalIncomeX, currentY, { align: 'right' });
          doc.text(formatCurrency(totals.income), margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5], currentY, { align: 'right' });
          currentY += 7;
          
          // Totale Uscite
          doc.text('Totale Uscite:', totalIncomeX, currentY, { align: 'right' });
          doc.text(formatCurrency(totals.expense), margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5], currentY, { align: 'right' });
          currentY += 7;
          
          // Saldo
          doc.setFontSize(11);
          doc.text('Saldo:', totalIncomeX, currentY, { align: 'right' });
          doc.text(formatCurrency(totals.balance), margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5], currentY, { align: 'right' });
        }
      }
      
      // Apri il PDF in una nuova finestra
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
      // Pulisci l'URL dopo un po'
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 100);
      
    } catch (error) {
      console.error('Errore nella generazione del PDF:', error);
      alert('Errore nella generazione del PDF. Riprova.');
    }
  };

  if (!canViewMovements) {
    return (
      <Alert severity="warning">
        Accesso negato: i movimenti contabili sono visibili solo agli amministratori.
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Registro Contabilità
      </Typography>

      {/* Filtri e Totali */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Filtri
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <DatePicker 
                    label="Data inizio"
                    value={dateRange.startDate}
                    onChange={(date) => handleDateRangeChange('startDate', date)}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                  <DatePicker 
                    label="Data fine"
                    value={dateRange.endDate}
                    onChange={(date) => handleDateRangeChange('endDate', date)}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </Box>
                <FormControl fullWidth size="small">
                  <InputLabel id="account-filter-label">Filtra per conto</InputLabel>
                  <Select
                    labelId="account-filter-label"
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    label="Filtra per conto"
                  >
                    <MenuItem value="all">Tutti i conti</MenuItem>
                    {accounts.map(account => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  label="Cerca nella descrizione"
                  value={descriptionSearch}
                  onChange={(e) => setDescriptionSearch(e.target.value)}
                  placeholder="Inserisci testo da cercare..."
                />
              </Box>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Totali
            </Typography>
            <Box>
            <Typography variant="body2" color="text.secondary">
              Saldo precedente: {formatCurrency(totals.previousBalance)}
            </Typography>
              <Typography variant="body1" color="success.main">
                Entrate periodo: {formatCurrency(totals.income)}
              </Typography>
              <Typography variant="body1" color="error.main">
                Uscite periodo: {formatCurrency(totals.expense)}
              </Typography>
              <Typography 
                variant="body1" 
                fontWeight="bold"
                color={totals.balance >= 0 ? 'success.main' : 'error.main'}
              >
                Saldo periodo: {formatCurrency(totals.balance)}
              </Typography>
              <Typography 
                variant="body1" 
                fontWeight="bold"
                sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}
                color={(totals.previousBalance + totals.balance) >= 0 ? 'success.main' : 'error.main'}
              >
                Saldo totale: {formatCurrency(totals.previousBalance + totals.balance)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', height: '100%', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleOpenModal}
              sx={{
                minWidth: 150,
                fontWeight: 'bold',
                fontSize: '0.9rem',
                py: 2,
                px: 3,
                color: '#fff',
                boxShadow: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textTransform: 'none',
                flexDirection: 'column',
                whiteSpace: 'normal',
                lineHeight: 1.0,
                height: 56,
              }}
            >
              <span style={{ display: 'block', textAlign: 'center' }}>Movimento<br />Manuale</span>
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<PrintIcon />}
              onClick={handleExportOrPrint}
              sx={{
                minWidth: 180,
                fontWeight: 'bold',
                fontSize: '0.9rem',
                py: 2,
                px: 3,
                color: '#fff',
                boxShadow: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textTransform: 'none',
                height: 56,
              }}
            >
              Esporta o Stampa
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabella Movimenti */}
      <TableContainer component={Paper}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ ml: 1 }}>
            {sortedMovements.length} movimenti
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              title={showFilters ? "Nascondi filtri" : "Mostra filtri"}
              color={showFilters ? "primary" : "default"}
            >
              <FilterList fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleResetColumnWidths}
              title="Ripristina larghezze colonne"
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel>Per pagina</InputLabel>
              <Select
                value={itemsPerPage}
                label="Per pagina"
                onChange={handleItemsPerPageChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <IconButton
                size="small"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                title="Pagina precedente"
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
                Pagina {currentPage} di {totalPages}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                title="Pagina successiva"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Table sx={{ minWidth: 1200, width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  width: `${columnWidths.date}px`,
                  minWidth: `${columnWidths.date}px`,
                  maxWidth: `${columnWidths.date}px`,
                  position: 'relative',
                  paddingRight: '4px'
                }}
              >
                <TableSortLabel
                  active={order.field === 'date'}
                  direction={order.field === 'date' ? order.direction : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Data
                </TableSortLabel>
                <Box
                  onMouseDown={(e) => handleResizeStart('date', e)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: resizingColumn === 'date' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </TableCell>
              <TableCell 
                sx={{ 
                  width: `${columnWidths.type}px`,
                  minWidth: `${columnWidths.type}px`,
                  maxWidth: `${columnWidths.type}px`,
                  position: 'relative',
                  paddingRight: '4px'
                }}
              >
                <TableSortLabel
                  active={order.field === 'type'}
                  direction={order.field === 'type' ? order.direction : 'asc'}
                  onClick={() => handleRequestSort('type')}
                >
                  Tipo
                </TableSortLabel>
                <Box
                  onMouseDown={(e) => handleResizeStart('type', e)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: resizingColumn === 'type' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </TableCell>
              <TableCell 
                sx={{ 
                  width: `${columnWidths.description}px`,
                  minWidth: `${columnWidths.description}px`,
                  maxWidth: `${columnWidths.description}px`,
                  position: 'relative',
                  paddingRight: '4px'
                }}
              >
                <TableSortLabel
                  active={order.field === 'description'}
                  direction={order.field === 'description' ? order.direction : 'asc'}
                  onClick={() => handleRequestSort('description')}
                >
                  Descrizione
                </TableSortLabel>
                <Box
                  onMouseDown={(e) => handleResizeStart('description', e)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: resizingColumn === 'description' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </TableCell>
              <TableCell 
                sx={{ 
                  width: `${columnWidths.paymentMethod}px`,
                  minWidth: `${columnWidths.paymentMethod}px`,
                  maxWidth: `${columnWidths.paymentMethod}px`,
                  position: 'relative',
                  paddingRight: '4px'
                }}
              >
                <TableSortLabel
                  active={order.field === 'paymentMethodId'}
                  direction={order.field === 'paymentMethodId' ? order.direction : 'asc'}
                  onClick={() => handleRequestSort('paymentMethodId')}
                >
                  Metodo di Pagamento
                </TableSortLabel>
                <Box
                  onMouseDown={(e) => handleResizeStart('paymentMethod', e)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: resizingColumn === 'paymentMethod' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </TableCell>
              <TableCell 
                sx={{ 
                  width: `${columnWidths.account}px`,
                  minWidth: `${columnWidths.account}px`,
                  maxWidth: `${columnWidths.account}px`,
                  position: 'relative',
                  paddingRight: '4px'
                }}
              >
                <TableSortLabel
                  active={order.field === 'accountId'}
                  direction={order.field === 'accountId' ? order.direction : 'asc'}
                  onClick={() => handleRequestSort('accountId')}
                >
                  Conto
                </TableSortLabel>
                <Box
                  onMouseDown={(e) => handleResizeStart('account', e)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: resizingColumn === 'account' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  width: `${columnWidths.amount}px`,
                  minWidth: `${columnWidths.amount}px`,
                  maxWidth: `${columnWidths.amount}px`,
                  position: 'relative',
                  paddingRight: '4px'
                }}
              >
                <TableSortLabel
                  active={order.field === 'amount'}
                  direction={order.field === 'amount' ? order.direction : 'asc'}
                  onClick={() => handleRequestSort('amount')}
                >
                  Importo
                </TableSortLabel>
                <Box
                  onMouseDown={(e) => handleResizeStart('amount', e)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: resizingColumn === 'amount' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </TableCell>
              <TableCell 
                sx={{ 
                  width: `${columnWidths.source}px`,
                  minWidth: `${columnWidths.source}px`,
                  maxWidth: `${columnWidths.source}px`,
                  position: 'relative',
                  paddingRight: '4px'
                }}
              >
                <TableSortLabel
                  active={order.field === 'source'}
                  direction={order.field === 'source' ? order.direction : 'asc'}
                  onClick={() => handleRequestSort('source')}
                >
                  Origine
                </TableSortLabel>
                <Box
                  onMouseDown={(e) => handleResizeStart('source', e)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: resizingColumn === 'source' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </TableCell>
              <TableCell 
                sx={{ 
                  width: `${columnWidths.flag}px`,
                  minWidth: `${columnWidths.flag}px`,
                  maxWidth: `${columnWidths.flag}px`,
                  position: 'relative',
                  paddingRight: '4px'
                }}
              >
                Flag
                <Box
                  onMouseDown={(e) => handleResizeStart('flag', e)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: resizingColumn === 'flag' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  width: `${columnWidths.actions}px`,
                  minWidth: `${columnWidths.actions}px`,
                  maxWidth: `${columnWidths.actions}px`
                }}
              >
                Azioni
              </TableCell>
            </TableRow>
            {showFilters && (
              <TableRow>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.date}px`,
                    minWidth: `${columnWidths.date}px`,
                    maxWidth: `${columnWidths.date}px`
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton 
                      size="small" 
                      onClick={handleClearFilters}
                      title="Pulisci filtri"
                      disabled={Object.values(columnFilters).every(f => !f)}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                    <TextField
                      size="small"
                      placeholder="Filtra data..."
                      value={columnFilters.date}
                      onChange={(e) => handleFilterChange('date', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.type}px`,
                    minWidth: `${columnWidths.type}px`,
                    maxWidth: `${columnWidths.type}px`
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Filtra tipo..."
                    value={columnFilters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.description}px`,
                    minWidth: `${columnWidths.description}px`,
                    maxWidth: `${columnWidths.description}px`
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Filtra descrizione..."
                    value={columnFilters.description}
                    onChange={(e) => handleFilterChange('description', e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.paymentMethod}px`,
                    minWidth: `${columnWidths.paymentMethod}px`,
                    maxWidth: `${columnWidths.paymentMethod}px`
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Filtra metodo..."
                    value={columnFilters.paymentMethod}
                    onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.account}px`,
                    minWidth: `${columnWidths.account}px`,
                    maxWidth: `${columnWidths.account}px`
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Filtra conto..."
                    value={columnFilters.account}
                    onChange={(e) => handleFilterChange('account', e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.amount}px`,
                    minWidth: `${columnWidths.amount}px`,
                    maxWidth: `${columnWidths.amount}px`
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Filtra importo..."
                    value={columnFilters.amount}
                    onChange={(e) => handleFilterChange('amount', e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.source}px`,
                    minWidth: `${columnWidths.source}px`,
                    maxWidth: `${columnWidths.source}px`
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Filtra origine..."
                    value={columnFilters.source}
                    onChange={(e) => handleFilterChange('source', e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.flag}px`,
                    minWidth: `${columnWidths.flag}px`,
                    maxWidth: `${columnWidths.flag}px`
                  }}
                >
                  <FormControl size="small" fullWidth>
                    <Select
                      value={columnFilters.flag}
                      onChange={(e) => handleFilterChange('flag', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="all">Tutti</MenuItem>
                      <MenuItem value="selected">Selezionati</MenuItem>
                      <MenuItem value="notSelected">Non selezionati</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.actions}px`,
                    minWidth: `${columnWidths.actions}px`,
                    maxWidth: `${columnWidths.actions}px`
                  }}
                />
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {paginatedMovements.map((movement) => (
              <TableRow 
                key={movement.id}
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  color: movement.type === 'IN' ? 'success.main' : 'error.main'
                }}
              >
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.date}px`,
                    minWidth: `${columnWidths.date}px`,
                    maxWidth: `${columnWidths.date}px`
                  }}
                >
                  {format(parseISO(movement.date), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell 
                  sx={{ 
                    color: movement.type === 'IN' ? 'success.main' : 'error.main',
                    width: `${columnWidths.type}px`,
                    minWidth: `${columnWidths.type}px`,
                    maxWidth: `${columnWidths.type}px`
                  }}
                >
                  {getMovementTypeText(movement.type)}
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.description}px`,
                    minWidth: `${columnWidths.description}px`,
                    maxWidth: `${columnWidths.description}px`
                  }}
                >
                  <Box>
                    {movement.description}
                    {movement.modificationNote && (
                      <Typography
                        variant="caption"
                        component="div"
                        color="warning.main"
                        sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                      >
                        {movement.modificationNote}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.paymentMethod}px`,
                    minWidth: `${columnWidths.paymentMethod}px`,
                    maxWidth: `${columnWidths.paymentMethod}px`
                  }}
                >
                  {getPaymentMethodName(movement.paymentMethodId)}
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.account}px`,
                    minWidth: `${columnWidths.account}px`,
                    maxWidth: `${columnWidths.account}px`
                  }}
                >
                  {getAccountName(movement.accountId)}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    color: movement.type === 'IN' ? 'success.main' : 'error.main',
                    width: `${columnWidths.amount}px`,
                    minWidth: `${columnWidths.amount}px`,
                    maxWidth: `${columnWidths.amount}px`
                  }}
                >
                  {formatCurrency(movement.amount)}
                </TableCell>
                <TableCell 
                  sx={{ 
                    width: `${columnWidths.source}px`,
                    minWidth: `${columnWidths.source}px`,
                    maxWidth: `${columnWidths.source}px`
                  }}
                >
                  {getSourceText(movement.source)}
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    width: `${columnWidths.flag}px`,
                    minWidth: `${columnWidths.flag}px`,
                    maxWidth: `${columnWidths.flag}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedFlags.has(movement.id)}
                    onChange={() => handleToggleFlag(movement.id)}
                    size="small"
                    color="primary"
                  />
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    width: `${columnWidths.actions}px`,
                    minWidth: `${columnWidths.actions}px`,
                    maxWidth: `${columnWidths.actions}px`
                  }}
                >
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={() => handleOpenEditModal(movement)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => handleOpenDeleteDialog(movement)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {sortedMovements.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Nessun movimento trovato per il periodo selezionato
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        {/* Controlli paginazione in basso */}
        {sortedMovements.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {startIndex + 1}-{Math.min(endIndex, sortedMovements.length)} di {sortedMovements.length} movimenti
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Per pagina</InputLabel>
                <Select
                  value={itemsPerPage}
                  label="Per pagina"
                  onChange={handleItemsPerPageChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  title="Pagina precedente"
                >
                  <ChevronLeft />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
                  Pagina {currentPage} di {totalPages}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  title="Pagina successiva"
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </TableContainer>

      {/* Modale per Aggiungere Movimento */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1
        }}>
          <Typography id="modal-modal-title" variant="h6" component="h2" gutterBottom>
            {editMovement ? 'Modifica movimento contabile' : 'Aggiungi Movimento Manuale'}
          </Typography>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: 'La descrizione è obbligatoria' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Descrizione"
                      fullWidth
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="amount"
                  control={control}
                  rules={{ 
                    required: 'L\'importo è obbligatorio',
                    pattern: {
                      value: /^[0-9]+(\.[0-9]{1,2})?$/,
                      message: 'Inserisci un importo valido'
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Importo"
                      fullWidth
                      error={!!errors.amount}
                      helperText={errors.amount?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                  <Controller
                    name="date"
                    control={control}
                    rules={{ required: 'La data è obbligatoria' }}
                    render={({ field }) => (
                      <DatePicker
                        label="Data"
                        value={field.value}
                        onChange={(date) => field.onChange(date)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={!!errors.date}
                            helperText={errors.date?.message}
                          />
                        )}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'Il tipo è obbligatorio' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.type}>
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        {...field}
                        label="Tipo"
                      >
                        <MenuItem value="IN">Entrata</MenuItem>
                        <MenuItem value="OUT">Uscita</MenuItem>
                      </Select>
                      {errors.type && <FormHelperText>{errors.type.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="paymentMethodId"
                  control={control}
                  rules={{ required: 'Il metodo di pagamento è obbligatorio' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.paymentMethodId}>
                      <InputLabel>Metodo di Pagamento</InputLabel>
                      <Select
                        {...field}
                        label="Metodo di Pagamento"
                      >
                        {paymentMethods.map(method => (
                          <MenuItem key={method.id} value={method.id}>
                            {method.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.paymentMethodId && <FormHelperText>{errors.paymentMethodId.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="accountId"
                  control={control}
                  rules={{ required: 'Il conto è obbligatorio' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.accountId}>
                      <InputLabel>Conto</InputLabel>
                      <Select
                        {...field}
                        label="Conto"
                      >
                        {accounts.map(account => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.accountId && <FormHelperText>{errors.accountId.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              {editMovement && watch('type') === 'IN' && (
                <Grid item xs={12}>
                  <Controller
                    name="commissioni"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Commissioni incasso (€) – movimento in uscita"
                        fullWidth
                        placeholder="Opzionale, override manuale"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText="Se lasci vuoto, in creazione si applica la % del metodo di pagamento. Non usare su uscite."
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{ mr: 1 }}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Salva
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Modal>

      {/* Dialog di conferma eliminazione */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          Sei sicuro di voler eliminare questo movimento contabile?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annulla</Button>
          <Button onClick={handleDeleteMovement} color="error" variant="contained">Elimina</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per scelta Esporta o Stampa */}
      <Dialog open={openExportDialog} onClose={() => setOpenExportDialog(false)}>
        <DialogTitle>Scegli un'opzione</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Come vuoi procedere?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<FileDownload />}
              onClick={handleExportToExcel}
              sx={{
                justifyContent: 'flex-start',
                py: 1.5,
                px: 2,
                textTransform: 'none'
              }}
            >
              Esporta in Excel
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                justifyContent: 'flex-start',
                py: 1.5,
                px: 2,
                textTransform: 'none'
              }}
            >
              Stampa PDF
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExportDialog(false)}>Annulla</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountingJournalPage; 